class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerAddon extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.cart-addon__button');
    this.button.addEventListener('click', this.onAddClick.bind(this));
  }

  onAddClick() {
    if (this.button.classList.contains('loading')) return;
    this.button.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const cartDrawer = document.querySelector('cart-drawer');

    const sectionIds = ['cart-icon-bubble'];
    if (mainCartItems) sectionIds.push(mainCartItems.dataset.id);
    if (mainCartFooter) sectionIds.push(mainCartFooter.dataset.id);
    if (cartDrawer) sectionIds.push('cart-drawer');

    const formData = new FormData();
    formData.append('id', this.dataset.variantId);
    formData.append('quantity', 1);
    formData.append('sections', sectionIds);
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) return;

        if (mainCartItems && response.sections[mainCartItems.dataset.id]) {
          this.swapContents(mainCartItems, response.sections[mainCartItems.dataset.id]);
        }

        if (mainCartFooter && response.sections[mainCartFooter.dataset.id]) {
          this.swapContents(mainCartFooter, response.sections[mainCartFooter.dataset.id]);
        }

        if (cartDrawer && response.sections['cart-drawer']) {
          this.swapDrawer(response.sections['cart-drawer']);
        }

        const cartIconBubble = document.getElementById('cart-icon-bubble');
        const cartIconBubbleHtml = response.sections['cart-icon-bubble'];
        if (cartIconBubble && cartIconBubbleHtml) {
          const source = new DOMParser().parseFromString(cartIconBubbleHtml, 'text/html').querySelector('.shopify-section');
          if (source) cartIconBubble.innerHTML = source.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (document.body.contains(this.button)) this.button.classList.remove('loading');
      });
  }

  swapContents(container, html) {
    const source = new DOMParser().parseFromString(html, 'text/html');
    const sourceInner = source.querySelector('.js-contents');
    const targetInner = container.querySelector('.js-contents');
    if (sourceInner && targetInner) {
      targetInner.innerHTML = sourceInner.innerHTML;
    } else {
      container.innerHTML = source.body.innerHTML;
    }
  }

  swapDrawer(html) {
    const source = new DOMParser().parseFromString(html, 'text/html');

    const items = document.querySelector('cart-drawer-items');
    const sourceItems = source.querySelector('cart-drawer-items');
    if (items && sourceItems) items.replaceWith(sourceItems);

    const footer = document.querySelector('.cart-drawer__footer');
    const sourceFooter = source.querySelector('.cart-drawer__footer');
    if (footer && sourceFooter) footer.replaceWith(sourceFooter);
  }
}

customElements.define('cart-drawer-addon', CartDrawerAddon);
